import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Lock, Loader2, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

export default function AuthModal() {
  const { loginOpen, closeLogin, login, onLoginSuccess } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome${res.user?.name ? ", " + res.user.name : ""}!`);
      setEmail("");
      setPassword("");
      onLoginSuccess(res.user);
    } else {
      setError(res.error);
    }
  };

  const inputCls =
    "w-full bg-white border border-ink/15 rounded-lg px-4 py-3 text-ink placeholder:text-ink/40 focus:border-flame outline-none transition-colors";

  return (
    <Dialog open={loginOpen} onOpenChange={(o) => (o ? null : closeLogin())}>
      <DialogContent className="sm:max-w-md bg-white border-ink/10" data-testid="auth-modal">
        <DialogHeader>
          <div className="grid place-items-center h-12 w-12 rounded-xl bg-ink text-white mb-2">
            <GraduationCap className="h-6 w-6" />
          </div>
          <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight text-ink">
            Student Login
          </DialogTitle>
          <DialogDescription className="text-ink/55 text-sm">
            Log in with your registered email to download study materials.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="login-form">
          <div>
            <label className="text-sm font-medium text-ink/70">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={`mt-1.5 ${inputCls}`}
              autoComplete="username"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-ink/70">Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className={`mt-1.5 ${inputCls}`}
              autoComplete="current-password"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600" data-testid="login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-flame text-white font-semibold px-6 py-3.5 rounded-full hover:bg-ink transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? "Logging in..." : "Log In"}
          </button>
          <p className="text-center text-xs text-ink/40">
            Access is restricted to enrolled students. Contact the academy to get your login.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
